/**
 * CRM options for the signup form ("What CRM does your company use?").
 *
 * The client asked for this question on BOTH signup paths — the Google/Apple
 * block at the top of the signup tab and the email/password form below it — so
 * the answer is captured no matter how the account is created.
 *
 * MIRRORED FILE. An identical copy lives at
 * applications_monorepo/api_backend_fastify_server/src/config/crm-options.ts.
 * Vite cannot import across the two apps, and the same duplicate-with-a-note
 * arrangement already backs config/subscription-plan-limits.ts. Keep the option
 * list AND normalizeCrmSelection byte-identical between them: the frontend
 * validates for the user, the backend validates authoritatively, and a drift
 * would make one of the two reject what the other just accepted.
 *
 * The option list and its order come from the client's onboarding mockup
 * (card attachment). `value` is what is stored in
 * user_profiles.crm_provider and is what the client's reporting groups on, so
 * these strings are a data contract — relabel freely, but never repoint a value.
 */

/** Sentinel option that reveals the free-text "Other CRM" field. */
export const CRM_OPTION_OTHER = 'other';

/** Max length of the free-text value stored in user_profiles.crm_provider_other. */
export const CRM_OTHER_MAX_LENGTH = 100;

export interface CrmOption {
  /** Stored in user_profiles.crm_provider. */
  value: string;
  /** Shown in the dropdown. */
  label: string;
}

export const CRM_OPTIONS: readonly CrmOption[] = [
  { value: 'housecall_pro', label: 'Housecall Pro' },
  { value: 'servicetitan', label: 'ServiceTitan' },
  { value: 'jobber', label: 'Jobber' },
  { value: 'workiz', label: 'Workiz' },
  { value: 'fieldedge', label: 'FieldEdge' },
  { value: 'service_fusion', label: 'Service Fusion' },
  { value: 'jobnimbus', label: 'JobNimbus' },
  { value: 'albi', label: 'Albi' },
  { value: 'cotality_dash', label: 'Cotality DASH' },
  { value: 'xcelerate', label: 'Xcelerate' },
  { value: 'psa', label: 'PSA' },
  { value: 'irestore', label: 'iRestore' },
  { value: 'restoration_manager', label: 'Restoration Manager' },
  { value: 'job_dox', label: 'Job-Dox' },
  { value: CRM_OPTION_OTHER, label: 'Other' },
] as const;

/** Is this one of the values we ship? Anything else is rejected, not stored. */
export const isKnownCrmProvider = (value: unknown): value is string =>
  typeof value === 'string' && CRM_OPTIONS.some((option) => option.value === value);

/** The pair actually written to user_profiles. */
export interface CrmSelection {
  crmProvider: string;
  /** Non-null ONLY when crmProvider === 'other'. */
  crmProviderOther: string | null;
}

export type CrmValidationCode =
  | 'CRM_REQUIRED'
  | 'INVALID_CRM_PROVIDER'
  | 'CRM_OTHER_REQUIRED'
  | 'CRM_OTHER_TOO_LONG';

export type CrmValidationResult =
  | { ok: true; value: CrmSelection }
  | { ok: false; code: CrmValidationCode; message: string };

/**
 * Validate + canonicalise a CRM answer. The single place both apps decide what
 * a valid answer is.
 *
 * Notably it FORCES crmProviderOther to null for every non-'other' provider
 * rather than trusting the caller, which is what makes "pick Other, type
 * something, switch back to Salesforce" leave no orphaned free text behind —
 * on the client, on the server, and in the row.
 */
export function normalizeCrmSelection(
  crmProvider: unknown,
  crmProviderOther: unknown
): CrmValidationResult {
  const provider = typeof crmProvider === 'string' ? crmProvider.trim() : '';

  if (!provider) {
    return {
      ok: false,
      code: 'CRM_REQUIRED',
      message: 'Please tell us what CRM your company uses',
    };
  }

  if (!isKnownCrmProvider(provider)) {
    return {
      ok: false,
      code: 'INVALID_CRM_PROVIDER',
      message: 'Please choose one of the listed CRM options',
    };
  }

  if (provider !== CRM_OPTION_OTHER) {
    return { ok: true, value: { crmProvider: provider, crmProviderOther: null } };
  }

  const other = typeof crmProviderOther === 'string' ? crmProviderOther.trim() : '';

  if (!other) {
    return {
      ok: false,
      code: 'CRM_OTHER_REQUIRED',
      message: 'Please enter the CRM you use',
    };
  }

  if (other.length > CRM_OTHER_MAX_LENGTH) {
    return {
      ok: false,
      code: 'CRM_OTHER_TOO_LONG',
      message: `Please keep the CRM name under ${CRM_OTHER_MAX_LENGTH} characters`,
    };
  }

  return { ok: true, value: { crmProvider: provider, crmProviderOther: other } };
}
